import React, { useEffect, useMemo, useState } from "react";
import {
  getCountFromServer,
  collection,
  query,
  getDocs,
  doc,
  getDoc,
  updateDoc,
  deleteDoc,
  where,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./MasterControlPanel.css";
import { ref as storageRef, deleteObject, listAll } from "firebase/storage";
import { storage } from "./firebase"; // make sure you export storage from your firebase.js


export default function MasterControlPanel() {
  const navigate = useNavigate();

  // --- State ---
  const [usersCount, setUsersCount] = useState(null);
  const [housesCount, setHousesCount] = useState(null);
  const [rentCount, setRentCount] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const [activeSidebar, setActiveSidebar] = useState(null);
  const [listIds, setListIds] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemDetails, setItemDetails] = useState(null);
  const [searchValue, setSearchValue] = useState("");

  const [allUsers, setAllUsers] = useState([]);
  const [assignUserId, setAssignUserId] = useState("");
  const [assignSearch, setAssignSearch] = useState("");
  const [loadingDetails, setLoadingDetails] = useState(false);

  const [tickets, setTickets] = useState([]);
  const [ticketLoading, setTicketLoading] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);

  const [topListings, setTopListings] = useState([]);
  const [topSearch, setTopSearch] = useState("");

  const [feedbacks, setFeedbacks] = useState([]);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [feedbackSearch, setFeedbackSearch] = useState("");

  const [rentBills, setRentBills] = useState([]);
  const [billsLoading, setBillsLoading] = useState(false);
  const [selectedListingForBills, setSelectedListingForBills] = useState(null);

  // Track which field is being edited
const [editingKey, setEditingKey] = useState(null); 
const [editingValue, setEditingValue] = useState(""); 



  // --- Admin check ---
  useEffect(() => {
    const checkAdmin = async () => {
      if (!auth.currentUser) return navigate("/login");
      try {
        const snap = await getDoc(doc(db, "user-data", auth.currentUser.uid));
        if (!snap.exists() || snap.data().role !== "admin") {
          toast.error("Unauthorized access");
          return navigate("/unauthorized");
        }
        setIsAdmin(true);
      } catch (err) {
        console.error(err);
        toast.error("Failed to verify admin");
        navigate("/unauthorized");
      }
    };
    checkAdmin();
  }, []);

const deleteStorageFolder = async (folderPath) => {
  try {
    const folderRef = storageRef(storage, folderPath);
    const list = await listAll(folderRef);

    // Delete all files in this folder
    const fileDeletes = list.items.map((fileRef) => deleteObject(fileRef));
    await Promise.all(fileDeletes);

    // Recursively delete subfolders if any
    const folderDeletes = list.prefixes.map((subFolderRef) =>
      deleteStorageFolder(subFolderRef.fullPath)
    );
    await Promise.all(folderDeletes);

    console.log(`Deleted all files in ${folderPath}`);
  } catch (err) {
    if (!err.message.includes("Object does not exist")) {
      console.error("Failed to delete storage folder", folderPath, err);
      toast.error(`Failed to delete files in ${folderPath}`);
    }
  }
};


  // --- Fetch counts ---
  const fetchCounts = async () => {
    try {
      setLoading(true);
      const [usersSnap, housesSnap, rentSnap] = await Promise.all([
        getCountFromServer(query(collection(db, "user-data"))),
        getCountFromServer(query(collection(db, "house-listings"))),
        getCountFromServer(query(collection(db, "rent-listings"))),
      ]);
      setUsersCount(usersSnap.data().count ?? 0);
      setHousesCount(housesSnap.data().count ?? 0);
      setRentCount(rentSnap.data().count ?? 0);
      setLastUpdated(new Date());
    } catch (err) {
      console.error(err);
      setError(err instanceof Error ? err.message : "Failed to fetch counts.");
      toast.error("Failed to fetch counts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCounts();
    loadTopListings();
  }, []);

  // --- Load all IDs ---
  const loadAll = async (type) => {
    try {
      setActiveSidebar(type);
      setSelectedItem(null);
      setItemDetails(null);
      setSearchValue("");
      setAssignSearch("");

      const colName =
        type === "users" ? "user-data" : type === "houses" ? "house-listings" : "rent-listings";
      const snap = await getDocs(query(collection(db, colName)));
      setListIds(snap.docs.map((d) => d.id));

      if (type === "houses" || type === "rent") {
        const userSnap = await getDocs(query(collection(db, "user-data")));
        setAllUsers(
          userSnap.docs.map((d) => ({
            id: d.id,
            customId: d.data().customId || "",
            email: d.data().email || "",
            ...d.data(),
          }))
        );
      } else setAllUsers([]);
    } catch (err) {
      console.error(err);
      setError(`Failed to load ${type} IDs.`);
      toast.error(`Failed to load ${type} IDs`);
    }
  };

const deleteListing = async (collectionName, listingId) => {
  if (!window.confirm(`Delete ${listingId} from ${collectionName}? This will remove ALL data.`)) return;

  try {
    // Fetch the listing document
    const listingDoc = await getDoc(doc(db, collectionName, listingId));
    if (!listingDoc.exists()) throw new Error("Listing not found");
    const data = listingDoc.data();

    // Collect all Storage paths to delete
    const pathsToDelete = [];

    if (data.coverPath) pathsToDelete.push(data.coverPath);
    if (data.thumbnailPath) pathsToDelete.push(data.thumbnailPath);
    if (data.imagesPaths?.length) pathsToDelete.push(...data.imagesPaths);
    if (data.slotImagePaths?.length) pathsToDelete.push(...data.slotImagePaths);
    if (data.videoPath) pathsToDelete.push(data.videoPath);

    // Delete each file in Storage
    for (const path of pathsToDelete) {
      try {
        await deleteObject(storageRef(storage, path));
        console.log(`Deleted: ${path}`);
      } catch (err) {
        if (!err.message.includes("Object does not exist")) console.error(err);
      }
    }

    // Delete bills subcollection if exists
    const billsSnap = await getDocs(collection(db, `${collectionName}/${listingId}/bills`));
    for (const bill of billsSnap.docs) {
      await deleteDoc(doc(db, `${collectionName}/${listingId}/bills`, bill.id));
    }

    // Delete the listing document
    await deleteDoc(doc(db, collectionName, listingId));

    toast.success(`Deleted ${listingId} from ${collectionName}`);
    loadAll(collectionName === "house-listings" ? "houses" : "rent");
  } catch (err) {
    console.error(err);
    toast.error(`Failed to delete ${listingId}`);
  }
};

  
  // --- Load details ---
  const loadDetails = async (type, id) => {
    try {
      setLoadingDetails(true);
      setSelectedItem(id);

      const colName =
        type === "users" ? "user-data" : type === "houses" ? "house-listings" : "rent-listings";
      const snap = await getDoc(doc(db, colName, id));
      let details = snap.exists() ? snap.data() : { error: `${type} not found` };

      if (type === "users" && snap.exists()) {
        const housesSnap = await getDocs(
          query(collection(db, "house-listings"), where("ownerId", "==", id))
        );
        const rentSnap = await getDocs(
          query(collection(db, "rent-listings"), where("ownerId", "==", id))
        );
        details.assignedHouses = housesSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        details.assignedRent = rentSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      }

      setItemDetails(details);

      if (type === "houses" || type === "rent") {
        await loadRentBills(id, colName);
      }
    } catch (err) {
      console.error(err);
      setItemDetails({ error: `Failed to fetch ${type} details` });
      toast.error(`Failed to fetch ${type} details`);
    } finally {
      setLoadingDetails(false);
    }
  };

  // --- Load rent bills ---
  const loadRentBills = async (listingId, collectionName) => {
    try {
      setBillsLoading(true);
      setSelectedListingForBills(listingId);

      const billsSnap = await getDocs(
        collection(db, `${collectionName}/${listingId}/bills`)
      );
      const billsData = billsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRentBills(billsData);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load bills");
    } finally {
      setBillsLoading(false);
    }
  };

  // --- Search ---
  const handleSearch = async () => {
    if (!searchValue.trim()) return;
    try {
      setItemDetails(null);
      setSelectedItem(null);
      const colName =
        activeSidebar === "users"
          ? "user-data"
          : activeSidebar === "houses"
          ? "house-listings"
          : "rent-listings";

      const directSnap = await getDoc(doc(db, colName, searchValue.trim()));
      if (directSnap.exists()) {
        setListIds([searchValue.trim()]);
        return;
      }

      if (activeSidebar === "users") {
        const snap = await getDocs(
          query(collection(db, colName), where("customId", "==", searchValue.trim()))
        );
        const snapEmail = await getDocs(
          query(collection(db, colName), where("email", "==", searchValue.trim()))
        );
        const combined = [...snap.docs, ...snapEmail.docs];
        setListIds(combined.length ? combined.map((d) => d.id) : []);
      } else setListIds([]);
    } catch (err) {
      console.error(err);
      setError("Search failed.");
      toast.error("Search failed");
    }
  };

  // --- Assign listing to user ---
const assignToUser = async () => {
  if (!assignUserId || !selectedItem || !activeSidebar) return;

  try {
    // Get user details
    const userSnap = await getDoc(doc(db, "user-data", assignUserId));
    if (!userSnap.exists()) throw new Error("User not found");

    const userData = userSnap.data();

    const colName = activeSidebar === "houses" ? "house-listings" : "rent-listings";

    await updateDoc(doc(db, colName, selectedItem), { 
      ownerId: assignUserId,
      assignedTo: assignUserId,
      email: userData.email || ""
    });

    toast.success(`Assigned ${selectedItem} to user ${assignUserId}`);
    loadDetails(activeSidebar, selectedItem);
  } catch (err) {
    console.error(err);
    toast.error("Failed to assign listing");
  }
};

  // --- Load tickets ---
  const loadTickets = async () => {
    try {
      setActiveSidebar("tickets");
      setTicketLoading(true);
      const snap = await getDocs(query(collection(db, "tickets")));
      const data = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
      setTickets(data);
      setTicketLoading(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch tickets/reports");
      setTicketLoading(false);
    }
  };

  // --- Update ticket status ---
  const updateTicketStatus = async (docId, newStatus) => {
    if (!isAdmin) return toast.error("No permission");
    try {
      if (newStatus === "closed") {
        await deleteDoc(doc(db, "tickets", docId));
        toast.success("Ticket closed and deleted");
      } else {
        await updateDoc(doc(db, "tickets", docId), { status: newStatus });
        toast.success(`Ticket status updated to ${newStatus}`);
      }
      loadTickets();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update ticket status");
    }
  };

  const goToChooseListing = () => {
    navigate("/homepage/choose-listing", { state: { fromMasterPanel: true } });
  };

  // --- Load Top 20 Listings ---
  const loadTopListings = async () => {
    try {
      const houseSnap = await getDocs(query(collection(db, "house-listings")));
      const rentSnap = await getDocs(query(collection(db, "rent-listings")));

      const combined = [
        ...houseSnap.docs.map((d) => ({ id: d.id, collection: "house-listings", ...d.data() })),
        ...rentSnap.docs.map((d) => ({ id: d.id, collection: "rent-listings", ...d.data() })),
      ];

      const sorted = combined.sort((a, b) => (b.views || 0) - (a.views || 0));
      setTopListings(sorted.slice(0, 20));
    } catch (err) {
      console.error(err);
      toast.error("Failed to load Top 20 Listings");
    }
  };

  // --- Filter helpers ---
  const filteredUsers = allUsers.filter(
    (u) =>
      u.customId.toLowerCase().includes(assignSearch.toLowerCase()) ||
      u.id.toLowerCase().includes(assignSearch.toLowerCase()) ||
      (u.email && u.email.toLowerCase().includes(assignSearch.toLowerCase()))
  );

  const filteredTopListings = topListings.filter(
    (l) =>
      l.id.toLowerCase().includes(topSearch.toLowerCase()) ||
      (l.pgName && l.pgName.toLowerCase().includes(topSearch.toLowerCase())) ||
      (l.name && l.name.toLowerCase().includes(topSearch.toLowerCase()))
  );

  const cards = useMemo(
    () => [
      { key: "users", label: "Total Users", value: usersCount, onClick: () => loadAll("users") },
      { key: "houses", label: "House Listings", value: housesCount, onClick: () => loadAll("houses") },
      { key: "rent", label: "Rent Listings", value: rentCount, onClick: () => loadAll("rent") },
      { key: "tickets", label: "Reports/Tickets", value: tickets.length, onClick: () => loadTickets() },
      { key: "feedback", label: "User Feedbacks", value: feedbacks.length, onClick: () => loadFeedbacks() },
    ],
    [usersCount, housesCount, rentCount, tickets.length, feedbacks.length]
  );

  const filteredFeedbacks = feedbacks.filter((fb) => {
    const search = feedbackSearch.toLowerCase();
    return (
      (fb.username && fb.username.toLowerCase().includes(search)) ||
      (fb.userId && fb.userId.toLowerCase().includes(search)) ||
      (fb.feedback && fb.feedback.toLowerCase().includes(search))
    );
  });

  // --- Load feedbacks ---
  const loadFeedbacks = async () => {
    try {
      setActiveSidebar("feedback");
      setFeedbackLoading(true);
      const snap = await getDocs(collection(db, "feedback"));
      const data = snap.docs.map((d) => ({ docId: d.id, ...d.data() }));
      setFeedbacks(data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch feedbacks");
    } finally {
      setFeedbackLoading(false);
    }
  };

  // --- Delete feedback ---
  const deleteFeedback = async (docId) => {
    if (!window.confirm("Delete this feedback?")) return;
    try {
      await deleteDoc(doc(db, "feedback", docId));
      toast.success("Feedback deleted");
      loadFeedbacks();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete feedback");
    }
  };

  const startEditingField = (key) => {
  setEditingKey(key);                // Set the field as active for editing
  setEditingValue(itemDetails[key] ?? ""); // Load current value
};

const saveFieldEdit = async (key) => {
  if (!selectedItem || !activeSidebar) return; // Safety check

  try {
    const colName =
      activeSidebar === "users"
        ? "user-data"
        : activeSidebar === "houses"
        ? "house-listings"
        : "rent-listings";

    // Update Firestore
    await updateDoc(doc(db, colName, selectedItem), {
      [key]: editingValue
    });

    // Update local state immediately
    setItemDetails((prev) => ({ ...prev, [key]: editingValue }));

    toast.success(`${key} updated successfully`);
    setEditingKey(null); // Stop editing
  } catch (err) {
    console.error(err);
    toast.error(`Failed to update ${key}`);
  }
};

  return (
    <div className="master-panel">
      <ToastContainer position="top-right" />
      <div className="panel-header">
        <div>
          <h2>Master Control Panel</h2>
          <p>Quick totals from Firestore</p>
        </div>
        <button onClick={fetchCounts} disabled={loading} className="refresh-btn">
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

  <div className="top-buttons">
  <button className="choose-listing-btn" onClick={goToChooseListing}>
    Create New Listing
  </button>

<button
  className="verify-bills-btn"
  onClick={() => navigate("/admin/housebills")}
>
  VERIFY BILLS
</button>
</div>


      {error && <div className="error-box">{error}</div>}

      <div className="cards">
        {cards.map((c) => (
          <div key={c.key} className="card">
            <div className="label">{c.label}</div>
            <button className="count-btn" onClick={c.onClick}>
              <span className="count-number">{c.value ?? "—"}</span>
              <span className="count-text">tap to view</span>
            </button>
          </div>
        ))}
      </div>

      <div className="updated-text">
        {lastUpdated ? `Last updated: ${lastUpdated.toLocaleString()}` : "Loading…"}
      </div>

      {/* Top 20 Listings */}
      <div className="top20-section">
        <h3>Top 20 Listings (Most Viewed)</h3>
        <input
          type="text"
          placeholder="Search by ID or Name"
          value={topSearch}
          onChange={(e) => setTopSearch(e.target.value)}
          className="search-input"
        />
        <div className="top20-grid">
          {filteredTopListings.length === 0 ? (
            <p>No listings match your search.</p>
          ) : (
            filteredTopListings.map((listing) => (
              <div key={listing.id} className="top20-card">
                <img
                  src={listing.thumbnailPath || (listing.slotImagePaths?.[0] ?? "https://via.placeholder.com/300")}
                  alt={listing.pgName || listing.name || "Listing"}
                />
                {listing.isPremium && <span className="premium-badge">Premium</span>}
                <div className="card-body">
                  <h4>{listing.pgName || listing.name || "Untitled"}</h4>
                  <p>{listing.address || "No address"}</p>
                  <p className="price">₹{listing.rent?.toLocaleString() ?? "N/A"}</p>
                  <div className="meta">
                    {listing.roomType && <span>{listing.roomType}</span>}
                    {listing.occupancy && <span>{listing.occupancy}</span>}
                    {listing.isVerified && <span>✔ Verified</span>}
                  </div>
                  <button
                    onClick={async () => {
                      try {
                        await updateDoc(doc(db, listing.collection, listing.id), {
                          showOnHomepage: !listing.showOnHomepage,
                          updatedAt: new Date().toISOString(),
                        });
                        toast.success(
                          listing.showOnHomepage ? "Removed from homepage" : "Added to homepage"
                        );
                        loadTopListings();
                      } catch (err) {
                        console.error(err);
                        toast.error("Failed to update homepage flag");
                      }
                    }}
                    className={`homepage-toggle ${listing.showOnHomepage ? "active" : ""}`}
                  >
                    {listing.showOnHomepage ? "Remove from Homepage" : "Add to Homepage"}
                  </button>
                  <div className="actions">
                    {listing.whatsapp && (
                      <a href={`https://wa.me/${listing.whatsapp}`} target="_blank" rel="noopener noreferrer">
                        WhatsApp
                      </a>
                    )}
                    {listing.locationUrl && (
                      <a href={listing.locationUrl} target="_blank" rel="noopener noreferrer">
                        View Map
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Sidebar Sections */}
      <div className={`sidebar ${activeSidebar ? "open" : ""}`}>
        {/* Users / Houses / Rent */}
        {(activeSidebar === "users" || activeSidebar === "houses" || activeSidebar === "rent") && (
          <div>
            <div className="sidebar-header">
              <h3>
                {activeSidebar === "users"
                  ? "All Users"
                  : activeSidebar === "houses"
                  ? "House Listings"
                  : "Rent Listings"}

              </h3>
              <button className="error-box" onClick={() => setActiveSidebar(null)}>X</button>
            </div>

            <div className="search-section">
              <input
                type="text"
                placeholder="Search by UID / Custom ID / Email"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
              />
              <button onClick={handleSearch}>Search</button>
            </div>
         <ul className="list-id-sidebar">
  {listIds.map((id) => (
    <li key={id}>
      <span 
        onClick={() => loadDetails(activeSidebar, id)} 
        style={{ cursor: "pointer" }}
      >
        {id}
      </span>
      {(activeSidebar === "houses" || activeSidebar === "rent") && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteListing(
              activeSidebar === "houses" ? "house-listings" : "rent-listings",
              id
            );
          }}
          style={{
            marginLeft: "10px",
            color: "red",
            cursor: "pointer",
            border: "none",
            background: "transparent"
          }}
        >
          🗑
        </button>
      )}
    </li>
  ))}
</ul>


            {itemDetails && (
              <div className="item-details">
                {/* Rent Bills Section */}
               {/* Rent Bills Section as Slots */}
{(activeSidebar === "houses" || activeSidebar === "rent") && rentBills.length > 0 && (
  <div className="assigned-section">
    <h4>Bills / Slot Images</h4>
    <div className="bills-container">
      {rentBills.map((bill) => (
        <div key={bill.id} className="bill-slot-card">
          {bill.billURL ? (
            <img
              src={bill.billURL}
              alt={bill.fileName || "Bill Image"}
              className="bill-slot-image"
              onClick={() => window.open(bill.billURL, "_blank")}
            />
          ) : (
            <div className="no-bill">No Image</div>
          )}
          <div className="bill-slot-info">
            <span>{bill.fileName || bill.id}</span>
            <span>Amount: ₹{bill.amount ?? "—"}</span>
            <span>Status: {bill.status ?? "—"}</span>
          </div>
        </div>
      ))}
    </div>
  </div>
)}


             {Object.keys(itemDetails).map((k) =>
  k !== "assignedHouses" && k !== "assignedRent" ? (
    <p key={k}>
      <strong>{k}:</strong>{" "}
      {editingKey === k ? (
        <>
          <input
            type={typeof itemDetails[k] === "number" ? "number" : "text"}
            value={editingValue}
            onChange={(e) => setEditingValue(e.target.value)}
            onKeyDown={async (e) => {
              if (e.key === "Enter") await saveFieldEdit(k); // Save on Enter
              if (e.key === "Escape") setEditingKey(null);   // Cancel on Escape
            }}
          />
          <button onClick={() => saveFieldEdit(k)}>💾</button>
          <button onClick={() => setEditingKey(null)}>✖</button>
        </>
      ) : (
        <span 
          onClick={() => startEditingField(k)} 
          style={{ cursor: "pointer" }}
        >
          {JSON.stringify(itemDetails[k])}
        </span>
      )}
    </p>
  ) : null
)}

                {(activeSidebar === "houses" || activeSidebar === "rent") && (
                  <div className="assign-card">
                    <h4>Assign Listing to User</h4>
                    <input
                      type="text"
                      placeholder="Search User by UID / Custom ID / Email"
                      value={assignSearch}
                      onChange={(e) => setAssignSearch(e.target.value)}
                    />
                    <ul className="assign-user-list">
                      {filteredUsers.map((u) => (
                        <li
                          key={u.id}
                          onClick={() => setAssignUserId(u.id)}
                          className={assignUserId === u.id ? "selected" : ""}
                        >
                          {u.customId} | {u.email} | {u.id}
                        </li>
                      ))}
                    </ul>
                    <button onClick={assignToUser} className="assign-btn">Assign Listing</button>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Tickets */}
        {activeSidebar === "tickets" && (
          <>
            <div className="sidebar-header">
              <h3>All Reports / Tickets</h3>
              <button onClick={() => setActiveSidebar(null)}>X</button>
            </div>
            {ticketLoading ? <p>Loading tickets...</p> :
              tickets.length === 0 ? <p>No tickets/reports found.</p> :
              <div className="tickets-list">
                {tickets.map((ticket) => (
                  <div key={ticket.docId} className="ticket-card">
                    <div className="ticket-info">
                      <strong>{ticket.type === "listing" ? "Listing Report" : "Support Ticket"}</strong>
                      <p><strong>ID:</strong> {ticket.docId}</p>
                      <p><strong>User:</strong> {ticket.userId}</p>
                      {ticket.subject && <p><strong>Subject:</strong> {ticket.subject}</p>}
                      {ticket.listingId && <p><strong>Listing ID:</strong> {ticket.listingId}</p>}
                      <p><strong>Description:</strong> {ticket.description}</p>
                    </div>
                    <div className="ticket-status">
                      <label>Status:</label>
                      <select value={ticket.status || "pending"} onChange={(e) => updateTicketStatus(ticket.docId, e.target.value)} disabled={!isAdmin}>
                        <option value="pending">Pending</option>
                        <option value="in-progress">In Progress</option>
                        <option value="resolved">Resolved</option>
                        <option value="closed">Close Ticket</option>
                      </select>
                    </div>
                  </div>
                ))}
              </div>
            }
          </>
        )}

        {/* Feedbacks */}
        {activeSidebar === "feedback" && (
          <>
            <div className="sidebar-header">
              <h3>User Feedbacks</h3>
              <button onClick={() => setActiveSidebar(null)}>X</button>
            </div>

            <div className="search-section">
              <input
                type="text"
                placeholder="Search by Username, User ID, or Feedback"
                value={feedbackSearch}
                onChange={(e) => setFeedbackSearch(e.target.value)}
                className="search-input"
              />
            </div>

            {feedbackLoading ? <p>Loading feedbacks...</p> :
              filteredFeedbacks.length === 0 ? <p>No feedbacks match your search.</p> :
              <div className="feedbacks-list">
                {filteredFeedbacks.map((fb) => (
                  <div key={fb.docId} className="feedback-card">
                    <p><strong>User:</strong> {fb.username || fb.userId || "Anonymous"}</p>
                    <p><strong>Rating:</strong> {fb.stars ?? "—"} ⭐</p>
                    <p><strong>Message:</strong> {fb.feedback}</p>
                    {fb.timestamp && <p className="feedback-time"><small>{new Date(fb.timestamp.seconds * 1000).toLocaleString()}</small></p>}
                    <button onClick={() => deleteFeedback(fb.docId)} className="delete-btn">Delete</button>
                  </div>
                ))}
              </div>
            }
          </>
        )}
      </div>
    </div>
  );
}
